import React, { useState, useEffect, useMemo, useCallback } from 'react';
import PageHeader from '../headers/Header';
import {
  Search, Filter, FileText, FileSpreadsheet, ChevronUp, ChevronDown,
  GraduationCap, MapPin, Briefcase, Calendar, X, Download, RotateCcw
} from 'lucide-react';
import { getAllInactiveEmployees, getAllEmployees } from '../../services/EmployeeService';
import { getAllOffboardings, getEmployeeImage } from '../../services/HRstaff';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import marabesLogo from '../../assets/images/logos/Marabes.png';
import {
  Box, Typography, Card, CardContent, Grid, TextField, MenuItem,
  Select, FormControl, InputLabel, InputAdornment, Button, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, useTheme, LinearProgress, Tooltip, IconButton, Avatar
} from '@mui/material';

if (typeof jsPDF === 'function' && !jsPDF.prototype.autoTable) {
  jsPDF.prototype.autoTable = autoTable;
}

// ─── helpers ──────────────────────────────────────────────────────────────────

const getBase64FromUrl = (url) =>
  new Promise((resolve) => {
    if (!url) { resolve(null); return; }
    const img = new Image();
    img.setAttribute('crossOrigin', 'anonymous');
    img.src = url;
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      canvas.getContext('2d').drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => resolve(null);
  });

const fmt = (date) => {
  if (!date) return '—';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '—';
  return `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
};

const fmtUnder = (text) => {
  if (!text || typeof text !== 'string') return text || '—';
  return text.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const COLS = [
  { key: 'name',        label: 'Full Name',   sortable: true },
  { key: 'entryDate',   label: 'Entry Date',  sortable: true },
  { key: 'city',        label: 'City',         sortable: true },
  { key: 'exitDate',    label: 'Exit Date',    sortable: true },
  { key: 'position',    label: 'Position',     sortable: true },
  { key: 'reason',      label: 'Reason',       sortable: false },
];

// ─── component ────────────────────────────────────────────────────────────────

export default function AlumniGroup() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [rows, setRows]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);

  // filters
  const [search, setSearch]                 = useState('');
  const [cityFilter, setCityFilter]         = useState('All');
  const [positionFilter, setPositionFilter] = useState('All');
  const [exitFrom, setExitFrom]             = useState('');
  const [exitTo, setExitTo]                 = useState('');

  // sort
  const [sortKey, setSortKey]       = useState('exitDate');
  const [sortDir, setSortDir]       = useState('desc');

  // export
  const [exportingPdf, setExportingPdf]     = useState(false);
  const [exportingXlsx, setExportingXlsx]   = useState(false);

  // ── fetch ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch everything in parallel
        const [offboardings, activeData, inactiveData] = await Promise.all([
          getAllOffboardings(),
          getAllEmployees().catch(() => []),
          getAllInactiveEmployees().catch(() => []),
        ]);

        // Build a combined employee lookup (inactive + active) keyed by id and full name
        const allEmployees = [...(inactiveData || []), ...(activeData || [])];
        const empById   = {};
        const empByName = {};
        allEmployees.forEach((emp) => {
          const id   = emp.employeeId || emp.id;
          const name = (`${emp.firstName || ''} ${emp.lastName || ''}`.trim() || emp.employeeName || emp.fullName || emp.name || '').toLowerCase();
          if (id)   empById[id]     = emp;
          if (name) empByName[name] = emp;
        });

        // Helper: extract position from employee object (with deep lookup like InactiveEmployees)
        const extractPosition = (emp) => {
          if (!emp) return '—';
          if (emp.positions && Array.isArray(emp.positions) && emp.positions.length > 0) {
            const valid = emp.positions.filter(p => p && p.positionName);
            if (valid.length > 0) return valid.map(p => p.positionName).join('; ');
          }
          if (emp.contract && Array.isArray(emp.contract)) {
            for (const c of emp.contract) {
              if (c.employee?.positions && Array.isArray(c.employee.positions)) {
                const valid = c.employee.positions.filter(p => p && p.positionName);
                if (valid.length > 0) return valid.map(p => p.positionName).join('; ');
              }
            }
          }
          if (emp.payrolls && Array.isArray(emp.payrolls)) {
            for (const p of emp.payrolls) {
              if (p.employee?.positions && Array.isArray(p.employee.positions)) {
                const valid = p.employee.positions.filter(pos => pos && pos.positionName);
                if (valid.length > 0) return valid.map(pos => pos.positionName).join('; ');
              }
            }
          }
          if (emp.balances && Array.isArray(emp.balances)) {
            for (const b of emp.balances) {
              if (b.employee?.positions && Array.isArray(b.employee.positions)) {
                const valid = b.employee.positions.filter(pos => pos && pos.positionName);
                if (valid.length > 0) return valid.map(pos => pos.positionName).join('; ');
              }
            }
          }
          const single = emp.positionName || emp.position || emp.jobTitle || emp.job || emp.role || emp.title;
          if (single) return single;
          return '—';
        };

        // Start from offboarding records — only alumni=true
        const alumniRecords = offboardings.filter((rec) => Boolean(rec.alumni));

        const transformed = await Promise.all(alumniRecords.map(async (rec) => {
          const firstName = rec.employeeFirstName || rec.employee?.firstName || '';
          const lastName  = rec.employeeLastName  || rec.employee?.lastName || '';
          const fullName  = `${firstName} ${lastName}`.trim() || rec.employeeName || rec.employee?.employeeName || rec.employee?.fullName || 'Unknown';
          const lowerName = fullName.toLowerCase();
          
          let empId = rec.employee?.employeeId || rec.employeeId || rec.employee?.id;

          // Look up enriched employee data
          let emp = empById[empId] || empByName[lowerName] || null;

          if (!empId && emp) {
            empId = emp.employeeId || emp.id;
          }

          let imageBase64 = null;
          try {
            if (rec.imageUrl || rec.employee?.imageUrl || rec.employeeImageUrl) {
              imageBase64 = await getBase64FromUrl(rec.imageUrl || rec.employee?.imageUrl || rec.employeeImageUrl);
            } else if (empId) {
              const blobUrl = await getEmployeeImage(empId);
              if (blobUrl) {
                imageBase64 = await getBase64FromUrl(blobUrl);
              }
            }
          } catch (e) { /* ignore image fetch errors */ }

          return {
            id        : rec.offboardingId || rec.id || empId || fullName,
            name      : fullName,
            entryDate : emp?.createdDate || emp?.dateOfJoining || emp?.joiningDate || emp?.startDate || null,
            city      : fmtUnder(rec.employeeOfficeName  || emp?.officeName  || emp?.office?.name  || '—'),
            exitDate  : rec.exitDate || null,
            position  : extractPosition(emp) !== '—' ? extractPosition(emp) : (rec.employeePosition || rec.position || '—'),
            reason    : fmtUnder(rec.reasonForDeparture) || '—',
            department: fmtUnder(rec.employeeDepartmentName || emp?.departmentName || '—'),
            alumni    : true,
            image     : imageBase64,
          };
        }));

        if (mounted) setRows(transformed);
      } catch (err) {
        console.error('AlumniGroup fetch error:', err);
        if (mounted) setError('Failed to load alumni data. Please try again.');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // ── derived lists for filter dropdowns ────────────────────────────────────
  const cities    = useMemo(() => ['All', ...new Set(rows.map((r) => r.city).filter((c) => c && c !== '—'))].sort(), [rows]);
  const positions = useMemo(() => ['All', ...new Set(rows.map((r) => r.position).filter((p) => p && p !== '—'))].sort(), [rows]);

  // ── filtered & sorted rows ─────────────────────────────────────────────────
  const displayed = useMemo(() => {
    let data = [...rows];

    // text search
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      data = data.filter((r) =>
        r.name.toLowerCase().includes(q) ||
        r.city.toLowerCase().includes(q) ||
        r.position.toLowerCase().includes(q) ||
        r.reason.toLowerCase().includes(q)
      );
    }
    // city
    if (cityFilter !== 'All') data = data.filter((r) => r.city === cityFilter);
    // position
    if (positionFilter !== 'All') data = data.filter((r) => r.position === positionFilter);
    // exit date range
    if (exitFrom) {
      const from = new Date(exitFrom);
      data = data.filter((r) => r.exitDate && new Date(r.exitDate) >= from);
    }
    if (exitTo) {
      const to = new Date(exitTo);
      to.setHours(23, 59, 59, 999);
      data = data.filter((r) => r.exitDate && new Date(r.exitDate) <= to);
    }

    // sort
    data.sort((a, b) => {
      let va = a[sortKey], vb = b[sortKey];
      if (sortKey === 'entryDate' || sortKey === 'exitDate') {
        va = va ? new Date(va).getTime() : 0;
        vb = vb ? new Date(vb).getTime() : 0;
        return sortDir === 'asc' ? va - vb : vb - va;
      }
      va = (va || '').toString().toLowerCase();
      vb = (vb || '').toString().toLowerCase();
      return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
    });

    return data;
  }, [rows, search, cityFilter, positionFilter, exitFrom, exitTo, sortKey, sortDir]);

  const handleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
  };

  const resetFilters = () => {
    setSearch(''); setCityFilter('All'); setPositionFilter('All');
    setExitFrom(''); setExitTo('');
  };

  const hasFilters = search || cityFilter !== 'All' || positionFilter !== 'All' || exitFrom || exitTo;

  // ── XLSX export ────────────────────────────────────────────────────────────
  const exportExcel = useCallback(async () => {
    if (!displayed.length) return;
    setExportingXlsx(true);
    try {
      const workbook = new ExcelJS.Workbook();
      const ws = workbook.addWorksheet('Alumni Group');

      const logoBase64Data = await getBase64FromUrl(marabesLogo);
      const axoBase64Data = await getBase64FromUrl('/cLogo.png');

      // Style header background (light blue F4F8FC)
      for (let i = 1; i <= 4; i++) {
        ws.getRow(i).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF4F8FC' } };
      }

      if (logoBase64Data) {
        const logoId = workbook.addImage({ base64: logoBase64Data.split(',')[1], extension: 'png' });
        ws.addImage(logoId, { tl: { col: 0, row: 0.2 }, ext: { width: 150, height: 45 } });
      }
      if (axoBase64Data) {
        const axoId = workbook.addImage({ base64: axoBase64Data.split(',')[1], extension: 'png' });
        ws.addImage(axoId, { tl: { col: 4.5, row: 0.2 }, ext: { width: 150, height: 45 } });
      }

      // Title
      ws.mergeCells('A2:F2');
      const titleCell = ws.getCell('A2');
      titleCell.value = 'ALUMNI OVERVIEW';
      titleCell.font = { name: 'Arial', size: 18, bold: true, color: { argb: 'FF0F1E50' } };
      titleCell.alignment = { horizontal: 'center', vertical: 'middle' };

      ws.mergeCells('A3:F3');
      const subTitleCell = ws.getCell('A3');
      subTitleCell.value = `Generated: ${new Date().toLocaleDateString('en-GB')}  |  Total: ${displayed.length} employee(s)`;
      subTitleCell.font = { name: 'Arial', size: 10, color: { argb: 'FF646E82' } };
      subTitleCell.alignment = { horizontal: 'center', vertical: 'middle' };

      ws.getRow(1).height = 35; // space for logo top
      ws.getRow(2).height = 25; // title
      ws.getRow(3).height = 15; // subtitle
      ws.getRow(4).height = 10; // spacing

      // Table Header
      const headers = ['Full Name', 'Entry Date', 'City', 'Exit Date', 'Position', 'Reason'];
      const headerRow = ws.addRow(headers);
      headerRow.height = 25;
      headerRow.eachCell((cell) => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF141E32' } };
        cell.font = { color: { argb: 'FFFFFFFF' }, bold: true, size: 10 };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
      });

      // Data
      displayed.forEach((r) => {
        const row = ws.addRow([r.name, fmt(r.entryDate), r.city, fmt(r.exitDate), r.position, r.reason]);
        row.eachCell((cell, colNumber) => {
          cell.alignment = { vertical: 'middle', horizontal: colNumber === 2 || colNumber === 4 ? 'center' : 'left' };
          cell.font = { size: 10 };
          cell.border = {
            top: { style: 'thin', color: { argb: 'FFE2E8F0' } },
            bottom: { style: 'thin', color: { argb: 'FFE2E8F0' } },
            left: { style: 'thin', color: { argb: 'FFE2E8F0' } },
            right: { style: 'thin', color: { argb: 'FFE2E8F0' } },
          };
        });
      });

      ws.columns = [
        { width: 30 }, { width: 14 }, { width: 16 }, { width: 14 }, { width: 35 }, { width: 45 }
      ];

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(blob, `alumni_group_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (err) {
      console.error('Excel export error:', err);
    } finally {
      setExportingXlsx(false);
    }
  }, [displayed]);

  // ── PDF export ─────────────────────────────────────────────────────────────
  const exportPDF = useCallback(async () => {
    if (!displayed.length) return;
    setExportingPdf(true);
    try {
      const doc = new jsPDF('l', 'mm', 'a4');
      const pw  = doc.internal.pageSize.width;
      const ph  = doc.internal.pageSize.height;

      // Logo
      const logoBase64 = await getBase64FromUrl(marabesLogo);
      const axoBase64  = await getBase64FromUrl('/cLogo.png');

      // Header bar
      doc.setFillColor(244, 248, 252); // Light blue
      doc.rect(0, 0, pw, 32, 'F');

      if (logoBase64) {
        try { doc.addImage(logoBase64, 'PNG', 10, 6, 45, 18); } catch (e) { /* skip */ }
      }
      if (axoBase64) {
        try { doc.addImage(axoBase64, 'PNG', pw - 55, 6, 45, 18); } catch (e) { /* skip */ }
      }

      doc.setTextColor(15, 30, 80); // Dark navy blue
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(22);
      doc.text('ALUMNI OVERVIEW', pw / 2, 16, { align: 'center' });
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 110, 130);
      doc.text(`Generated: ${new Date().toLocaleDateString('en-GB')}  |  Total: ${displayed.length} employee(s)`, pw / 2, 24, { align: 'center' });

      // Filter summary
      if (hasFilters) {
        const parts = [];
        if (search) parts.push(`Search: "${search}"`);
        if (cityFilter !== 'All') parts.push(`City: ${cityFilter}`);
        if (positionFilter !== 'All') parts.push(`Position: ${positionFilter}`);
        if (exitFrom) parts.push(`Exit from: ${exitFrom}`);
        if (exitTo)   parts.push(`Exit to: ${exitTo}`);
        doc.setTextColor(80, 80, 80);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'italic');
        doc.text(`Filters: ${parts.join(' | ')}`, 10, 33);
      }

      // Table
      autoTable(doc, {
        startY: hasFilters ? 37 : 32,
        head: [['Photo', 'Full Name', 'Entry Date', 'City', 'Exit Date', 'Position', 'Reason']],
        body: displayed.map((r) => ['', r.name, fmt(r.entryDate), r.city, fmt(r.exitDate), r.position, r.reason]),
        headStyles: {
          fillColor: [20, 30, 50], // Very dark navy / black
          textColor: 255,
          fontStyle: 'bold',
          fontSize: 9,
          halign: 'center',
          valign: 'middle',
        },
        bodyStyles: { fontSize: 8.5, cellPadding: 3, minCellHeight: 14 },
        alternateRowStyles: { fillColor: [245, 247, 255] },
        columnStyles: {
          0: { cellWidth: 14, halign: 'center', valign: 'middle' },
          1: { cellWidth: 36, fontStyle: 'bold', valign: 'middle' },
          2: { cellWidth: 24, halign: 'center', valign: 'middle' },
          3: { cellWidth: 26, valign: 'middle' },
          4: { cellWidth: 24, halign: 'center', textColor: [220, 60, 60], valign: 'middle' },
          5: { cellWidth: 38, valign: 'middle' },
          6: { cellWidth: 'auto', valign: 'middle' },
        },
        didDrawCell: (data) => {
          if (data.column.index === 0 && data.cell.section === 'body') {
            const rowData = displayed[data.row.index];
            if (rowData && rowData.image) {
              try {
                // Determine image type based on base64 prefix
                const type = rowData.image.includes('image/png') ? 'PNG' : 'JPEG';
                doc.addImage(rowData.image, type, data.cell.x + 2, data.cell.y + 2, 10, 10);
              } catch (e) { /* skip */ }
            }
          }
        },
        margin: { left: 10, right: 10 },
        didDrawPage: (data) => {
          // Footer
          doc.setFillColor(244, 248, 252);
          doc.rect(0, ph - 12, pw, 12, 'F');
          doc.setTextColor(100, 110, 130);
          doc.setFontSize(8);
          doc.setFont('helvetica', 'normal');
          doc.text('Marabes Group — Confidential HR Document', pw / 2, ph - 5, { align: 'center' });
          doc.text(`Page ${data.pageNumber}`, pw - 20, ph - 5);
        },
      });

      doc.save(`alumni_group_${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (err) {
      console.error('PDF export error:', err);
    } finally {
      setExportingPdf(false);
    }
  }, [displayed, hasFilters, search, cityFilter, positionFilter, exitFrom, exitTo]);

  // ── styles ─────────────────────────────────────────────────────────────────
  const cardSx = {
    borderRadius: 3,
    boxShadow: isDark
      ? '0 4px 24px rgba(0,0,0,0.4)'
      : '0 4px 24px rgba(63,81,181,0.08)',
    border: `1px solid ${isDark ? 'rgba(63,81,181,0.15)' : 'rgba(63,81,181,0.12)'}`,
    overflow: 'hidden',
  };

  const headerCellSx = (key) => ({
    fontWeight: 700,
    fontSize: '0.78rem',
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    color: isDark ? '#7986cb' : '#1a237e',
    background: isDark ? 'rgba(63,81,181,0.1)' : 'rgba(63,81,181,0.06)',
    cursor: COLS.find((c) => c.key === key)?.sortable ? 'pointer' : 'default',
    userSelect: 'none',
    whiteSpace: 'nowrap',
    py: 1.5,
    px: 2,
    '&:hover': COLS.find((c) => c.key === key)?.sortable
      ? { background: isDark ? 'rgba(63,81,181,0.18)' : 'rgba(63,81,181,0.12)' }
      : {},
  });

  const SortIcon = ({ col }) => {
    if (!col.sortable) return null;
    if (sortKey !== col.key)
      return <ChevronDown size={13} style={{ opacity: 0.35, marginLeft: 4 }} />;
    return sortDir === 'asc'
      ? <ChevronUp size={13} style={{ color: '#3f51b5', marginLeft: 4 }} />
      : <ChevronDown size={13} style={{ color: '#3f51b5', marginLeft: 4 }} />;
  };

  // ── render ─────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ minHeight: '100vh', p: 3, bgcolor: 'background.default' }}>

      {/* ── Page Header ── */}
      <PageHeader
        title={
          <Box display="flex" alignItems="center" gap={1}>
            <GraduationCap size={24} />
            Alumni Group
          </Box>
        }
        subtitle="Former employees who are part of the alumni network"
        actions={
          <Box display="flex" gap={1}>
            <Button
              variant="outlined"
              startIcon={exportingXlsx ? null : <FileSpreadsheet size={16} />}
              disabled={exportingXlsx || !displayed.length}
              onClick={exportExcel}
              sx={{
                textTransform: 'none',
                borderRadius: '8px',
                fontWeight: 600,
                borderColor: '#22c55e',
                color: '#22c55e',
                '&:hover': { borderColor: '#16a34a', bgcolor: 'rgba(34,197,94,0.06)' },
              }}
            >
              {exportingXlsx ? 'Exporting…' : 'Excel'}
            </Button>
            <Button
              variant="contained"
              startIcon={exportingPdf ? null : <FileText size={16} />}
              disabled={exportingPdf || !displayed.length}
              onClick={exportPDF}
              sx={{
                textTransform: 'none',
                borderRadius: '8px',
                fontWeight: 600,
                bgcolor: '#3f51b5',
                '&:hover': { bgcolor: '#303f9f' },
              }}
            >
              {exportingPdf ? 'Generating…' : 'Export PDF'}
            </Button>
          </Box>
        }
      />

      {/* ── Filters ── */}
      <Card sx={{ ...cardSx, mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="subtitle1" fontWeight={700} display="flex" alignItems="center" gap={1} color="primary">
              <Filter size={18} /> Filters
            </Typography>
            {hasFilters && (
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<RotateCcw size={14} />}
                onClick={resetFilters}
                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
              >
                Reset All
              </Button>
            )}
          </Box>

          <Grid container spacing={2} alignItems="center">
            {/* Search */}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by name, city, position, reason…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search size={16} color={isDark ? '#94a3b8' : '#64748b'} />
                    </InputAdornment>
                  ),
                  endAdornment: search ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearch('')}>
                        <X size={14} />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
              />
            </Grid>

            {/* City */}
            <Grid item xs={6} sm={3} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>City</InputLabel>
                <Select
                  label="City"
                  value={cityFilter}
                  startAdornment={
                    <InputAdornment position="start">
                      <MapPin size={16} style={{ color: '#1976d2', marginRight: -4 }} />
                    </InputAdornment>
                  }
                  onChange={(e) => setCityFilter(e.target.value)}
                  sx={{ borderRadius: '8px' }}
                >
                  {cities.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>

            {/* Position */}
            <Grid item xs={6} sm={3} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Position</InputLabel>
                <Select
                  label="Position"
                  value={positionFilter}
                  startAdornment={
                    <InputAdornment position="start">
                      <Briefcase size={16} style={{ color: '#1976d2', marginRight: -4 }} />
                    </InputAdornment>
                  }
                  onChange={(e) => setPositionFilter(e.target.value)}
                  sx={{ borderRadius: '8px' }}
                >
                  {positions.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>

            {/* Exit date from */}
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Exit Date From"
                value={exitFrom}
                onChange={(e) => setExitFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Calendar size={14} color="#3f51b5" />
                    </InputAdornment>
                  ),
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
              />
            </Grid>

            {/* Exit date to */}
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Exit Date To"
                value={exitTo}
                onChange={(e) => setExitTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Calendar size={14} color="#3f51b5" />
                    </InputAdornment>
                  ),
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
              />
            </Grid>
          </Grid>

          {/* Active filters chips */}
          {hasFilters && (
            <Box display="flex" flexWrap="wrap" gap={0.75} mt={1.5}>
              {search && <Chip size="small" label={`Search: "${search}"`} onDelete={() => setSearch('')} color="primary" variant="outlined" />}
              {cityFilter !== 'All' && <Chip size="small" label={`City: ${cityFilter}`} onDelete={() => setCityFilter('All')} color="success" variant="outlined" />}
              {positionFilter !== 'All' && <Chip size="small" label={`Position: ${positionFilter}`} onDelete={() => setPositionFilter('All')} color="warning" variant="outlined" />}
              {exitFrom && <Chip size="small" label={`From: ${exitFrom}`} onDelete={() => setExitFrom('')} sx={{ borderColor: '#3f51b5', color: '#3f51b5' }} variant="outlined" />}
              {exitTo && <Chip size="small" label={`To: ${exitTo}`} onDelete={() => setExitTo('')} sx={{ borderColor: '#3f51b5', color: '#3f51b5' }} variant="outlined" />}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* ── Loading / Error ── */}
      {loading && <LinearProgress sx={{ borderRadius: 1, mb: 2, '& .MuiLinearProgress-bar': { bgcolor: '#3f51b5' } }} />}
      {error && (
        <Card sx={{ ...cardSx, mb: 3, border: '1px solid #fca5a5', bgcolor: isDark ? '#2d1b1b' : '#fef2f2' }}>
          <CardContent>
            <Typography color="error" fontWeight={600}>{error}</Typography>
          </CardContent>
        </Card>
      )}

      {/* ── Table ── */}
      {!loading && !error && (
        <Card sx={cardSx}>
          {/* Table header bar */}
          <Box
            sx={{
              px: 3, py: 2,
              background: isDark
                ? 'linear-gradient(135deg, rgba(63,81,181,0.2) 0%, rgba(48,63,159,0.1) 100%)'
                : 'linear-gradient(135deg, rgba(63,81,181,0.08) 0%, rgba(159,168,218,0.12) 100%)',
              borderBottom: `1px solid ${isDark ? 'rgba(63,81,181,0.2)' : 'rgba(63,81,181,0.15)'}`,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between'
            }}
          >
            <Box display="flex" alignItems="center" gap={1.5}>
              <GraduationCap size={20} color="#3f51b5" />
              <Typography fontWeight={700} color={isDark ? '#7986cb' : '#283593'}>
                Alumni Employees
              </Typography>
              <Chip
                size="small"
                label={`${displayed.length} record${displayed.length !== 1 ? 's' : ''}`}
                sx={{ bgcolor: '#3f51b5', color: '#fff', fontWeight: 700, fontSize: '0.72rem' }}
              />
            </Box>
            <Typography variant="caption" color="text.secondary">
              Click column header to sort
            </Typography>
          </Box>

          {displayed.length === 0 ? (
            <Box sx={{ py: 8, textAlign: 'center' }}>
              <GraduationCap size={52} color={isDark ? '#334155' : '#cbd5e1'} />
              <Typography variant="h6" color="text.secondary" mt={2} fontWeight={600}>
                No alumni found
              </Typography>
              <Typography variant="body2" color="text.disabled" mt={0.5}>
                {hasFilters ? 'Try adjusting your filters.' : 'No offboarding employees are marked as alumni yet.'}
              </Typography>
              {hasFilters && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={resetFilters}
                  sx={{ mt: 2, borderRadius: '8px', textTransform: 'none' }}
                >
                  Clear Filters
                </Button>
              )}
            </Box>
          ) : (
            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 0, overflowX: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        ...headerCellSx(null),
                        width: 52, textAlign: 'center',
                        cursor: 'default',
                      }}
                    >
                      #
                    </TableCell>
                    {COLS.map((col) => (
                      <TableCell
                        key={col.key}
                        sx={headerCellSx(col.key)}
                        onClick={() => col.sortable && handleSort(col.key)}
                      >
                        <Box display="flex" alignItems="center">
                          {col.label}
                          <SortIcon col={col} />
                        </Box>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayed.map((row, idx) => (
                    <TableRow
                      key={row.id || idx}
                      sx={{
                        '&:hover': {
                          bgcolor: isDark ? 'rgba(63,81,181,0.07)' : 'rgba(63,81,181,0.04)',
                        },
                        transition: 'background 0.15s',
                      }}
                    >
                      {/* # */}
                      <TableCell sx={{ textAlign: 'center', color: 'text.disabled', fontSize: '0.78rem', fontWeight: 600 }}>
                        {idx + 1}
                      </TableCell>

                      {/* Full Name */}
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
                        <Box display="flex" alignItems="center" gap={1.2}>
                          {row.image ? (
                            <Avatar src={row.image} sx={{ width: 34, height: 34, flexShrink: 0 }} />
                          ) : (
                            <Box
                              sx={{
                                width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                background: `linear-gradient(135deg, #3f51b5, #303f9f)`,
                                color: '#fff', fontWeight: 800, fontSize: '0.78rem',
                              }}
                            >
                              {row.name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase()}
                            </Box>
                          )}
                          {row.name}
                        </Box>
                      </TableCell>

                      {/* Entry Date */}
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <Calendar size={13} color="#10b981" />
                          <Typography variant="body2" color="#10b981" fontWeight={600}>
                            {fmt(row.entryDate)}
                          </Typography>
                        </Box>
                      </TableCell>

                      {/* City */}
                      <TableCell>
                        <Chip
                          size="small"
                          label={row.city}
                          icon={<MapPin size={11} />}
                          sx={{
                            bgcolor: isDark ? 'rgba(59,130,246,0.12)' : 'rgba(59,130,246,0.09)',
                            color: '#3b82f6', fontWeight: 600, fontSize: '0.75rem',
                            border: '1px solid rgba(59,130,246,0.2)',
                            '& .MuiChip-icon': { color: '#3b82f6' },
                          }}
                        />
                      </TableCell>

                      {/* Exit Date */}
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <Calendar size={13} color="#ef4444" />
                          <Typography variant="body2" color="#ef4444" fontWeight={600}>
                            {fmt(row.exitDate)}
                          </Typography>
                        </Box>
                      </TableCell>

                      {/* Position */}
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <Briefcase size={13} color="#f59e0b" />
                          <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.82rem' }}>
                            {row.position}
                          </Typography>
                        </Box>
                      </TableCell>

                      {/* Reason */}
                      <TableCell>
                        <Tooltip title={row.reason} placement="top" arrow>
                          <Chip
                            size="small"
                            label={row.reason.length > 30 ? `${row.reason.slice(0, 28)}…` : row.reason}
                            sx={{
                              bgcolor: isDark ? 'rgba(100,116,139,0.15)' : 'rgba(100,116,139,0.09)',
                              color: isDark ? '#94a3b8' : '#64748b',
                              fontWeight: 500, fontSize: '0.75rem',
                              border: `1px solid ${isDark ? 'rgba(100,116,139,0.25)' : 'rgba(100,116,139,0.18)'}`,
                              maxWidth: 200,
                            }}
                          />
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Footer count */}
          {displayed.length > 0 && (
            <Box
              sx={{
                px: 3, py: 1.5,
                borderTop: `1px solid ${isDark ? 'rgba(63,81,181,0.15)' : 'rgba(63,81,181,0.1)'}`,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}
            >
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                Showing {displayed.length} of {rows.length} alumni employees
              </Typography>
              <Box display="flex" gap={1}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<FileSpreadsheet size={13} />}
                  disabled={exportingXlsx}
                  onClick={exportExcel}
                  sx={{
                    textTransform: 'none', borderRadius: '6px', fontSize: '0.75rem',
                    borderColor: '#22c55e', color: '#22c55e',
                    '&:hover': { borderColor: '#16a34a', bgcolor: 'rgba(34,197,94,0.05)' },
                  }}
                >
                  {exportingXlsx ? '…' : 'Excel'}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<FileText size={13} />}
                  disabled={exportingPdf}
                  onClick={exportPDF}
                  sx={{
                    textTransform: 'none', borderRadius: '6px', fontSize: '0.75rem',
                    borderColor: '#3f51b5', color: '#3f51b5',
                    '&:hover': { borderColor: '#303f9f', bgcolor: 'rgba(63,81,181,0.05)' },
                  }}
                >
                  {exportingPdf ? '…' : 'PDF'}
                </Button>
              </Box>
            </Box>
          )}
        </Card>
      )}
    </Box>
  );
}
